import { topupReminderBody } from '@/domain/topupReminders';
import { customerDepositUrl } from '@/utils/customerLinks';
import { buildConfirmationCadence } from '@/utils/confirmationTemplates';
import { continuablePiecesFor } from '@/utils/pieceHelpers';
import { depositsForCustomer, outstandingOnDeposit } from '@/utils/depositHelpers';
import { aud } from '@/utils/format';
import type {
  Artist,
  Booking,
  CustomerRecord,
  Deposit,
  Piece,
  Studio,
  Waiver,
} from '@/domain/types';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/** The rule that fired, so the card can say why rather than just what. */
export type SuggestionKind =
  | 'topup'
  | 'expired-link'
  | 'slot-taken'
  | 'missed-call'
  | 'confirm-waiver'
  | 'reactivation';

export interface SuggestedReply {
  kind: SuggestionKind;
  /** Short name for the chip, e.g. "Top-up reminder". */
  label: string;
  /** The message itself, ready to send. */
  body: string;
  /** One line on what made this the suggestion. */
  why: string;
}

interface Ctx {
  customer: CustomerRecord;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  pieces: Record<string, Piece>;
  waivers: Record<string, Waiver>;
  artists: Record<string, Artist>;
  studios: Record<string, Studio>;
  nowIso: string;
}

function firstName(c: CustomerRecord): string {
  return c.preferredName || c.firstName;
}

// The customer.deposits back-link is unreliable, so resolve by customerId.
function ownDeposits(ctx: Ctx): Deposit[] {
  return depositsForCustomer(ctx.deposits, ctx.customer);
}

/** A deposit that is part-paid, or a live link running out of road. */
function unsettled(ctx: Ctx): Deposit | undefined {
  const now = new Date(ctx.nowIso).getTime();
  return ownDeposits(ctx).find((d) => {
    if (outstandingOnDeposit(d) === 0) return false;
    if (d.status === 'paid') return true; // part-paid, top-up owing
    if (d.status !== 'pending') return false;
    const left = new Date(d.expiresAt).getTime() - now;
    return left > 0 && left <= 2 * DAY;
  });
}

function expiredLink(ctx: Ctx): Deposit | undefined {
  const now = new Date(ctx.nowIso).getTime();
  const dead = ownDeposits(ctx).filter(
    (d) =>
      !d.resentAt &&
      (d.status === 'expired' ||
        (d.status === 'pending' && new Date(d.expiresAt).getTime() <= now)),
  );
  // tv-smyz: a link released because another client paid first outranks a
  // plain lapse, so "hold the same date" never promises a date that's gone.
  return dead.find((d) => d.releasedReason === 'slot-taken') ?? dead[0];
}

/** Inbound voice inside 72h with no outbound voice after it - queueEngine's rule. */
function missedCall(ctx: Ctx): boolean {
  const now = new Date(ctx.nowIso).getTime();
  const voiceIn = [...ctx.customer.conversation]
    .filter((e) => e.channel === 'voice' && e.direction === 'in')
    .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))[0];
  if (!voiceIn) return false;
  const at = new Date(voiceIn.timestamp).getTime();
  if (now - at > 72 * HOUR) return false;
  return !ctx.customer.conversation.some(
    (e) => e.channel === 'voice' && e.direction === 'out' && new Date(e.timestamp).getTime() > at,
  );
}

/** Next upcoming session, if any. */
function nextBooking(ctx: Ctx): Booking | undefined {
  const now = new Date(ctx.nowIso).getTime();
  return ctx.customer.bookings
    .map((id) => ctx.bookings[id])
    .filter((b): b is Booking => Boolean(b))
    .filter((b) => b.status === 'confirmed' || b.status === 'held')
    .filter((b) => new Date(b.start).getTime() > now)
    .sort((a, b) => (a.start > b.start ? 1 : -1))[0];
}

function waiverFor(ctx: Ctx, bookingId: string): Waiver | undefined {
  return Object.values(ctx.waivers).find((w) => w.bookingId === bookingId);
}

function daysSinceLastContact(ctx: Ctx): number | null {
  const latest = ctx.customer.conversation.reduce<number | null>((acc, e) => {
    const t = new Date(e.timestamp).getTime();
    return acc === null || t > acc ? t : acc;
  }, null);
  if (latest === null) return null;
  return (new Date(ctx.nowIso).getTime() - latest) / DAY;
}

/**
 * The one message most worth sending right now, or null when nothing obvious
 * applies. First match wins, ordered by how much money is at stake.
 *
 * Deliberately deterministic: every body comes from copy that already exists and
 * already sends elsewhere in the app, so the agent is approving a real studio
 * message rather than a generated one. No model call, no invented wording.
 */
export function suggestReply(ctx: Ctx): SuggestedReply | null {
  const name = firstName(ctx.customer);

  // 1 · Money already half-collected, or a live link about to lapse.
  const owing = unsettled(ctx);
  if (owing) {
    const booking = owing.bookingId ? ctx.bookings[owing.bookingId] : undefined;
    const outstanding = outstandingOnDeposit(owing);
    const hoursLeft = Math.round(
      (new Date(owing.expiresAt).getTime() - new Date(ctx.nowIso).getTime()) / HOUR,
    );
    return {
      kind: 'topup',
      label: 'Top-up reminder',
      body: topupReminderBody({
        firstName: name,
        outstandingAud: outstanding,
        sessionIso: booking?.start,
        linkUrl: customerDepositUrl(owing.id),
      }),
      why:
        owing.status === 'pending'
          ? `${aud(outstanding)} still to come and the link expires in ${hoursLeft}h`
          : `${aud(outstanding)} of the deposit is still outstanding`,
    };
  }

  // 2 · A link that has already lapsed and was never followed up.
  const dead = expiredLink(ctx);
  // bgg4 #1: a link released because another client paid for the slot first
  // didn't simply lapse; "hold the same date" would promise a date that's gone.
  if (dead?.releasedReason === 'slot-taken') {
    return {
      kind: 'slot-taken',
      label: 'New date',
      body: `Hi ${name}, so sorry - the date you were after was booked by another client before your deposit went through. Can I find you a new date?`,
      why: `The ${aud(dead.amountAud)} link was released: another client paid for that slot first`,
    };
  }
  if (dead) {
    return {
      kind: 'expired-link',
      label: 'Fresh link',
      body: `Hi ${name}, your deposit link expired before it went through. Want me to send a fresh one and hold the same date?`,
      why: `The ${aud(dead.amountAud)} link lapsed and has not been resent`,
    };
  }

  // 3 · They rang and nobody rang back.
  if (missedCall(ctx)) {
    return {
      kind: 'missed-call',
      label: 'Missed you',
      body: `Hi ${name}, sorry we missed your call. Happy to help now - what were you after?`,
      why: 'They called within the last 72h and there is no call back on file',
    };
  }

  // 4 · A session close enough to confirm, with the waiver still unsigned.
  const upcoming = nextBooking(ctx);
  if (upcoming) {
    const daysOut = (new Date(upcoming.start).getTime() - new Date(ctx.nowIso).getTime()) / DAY;
    const waiver = waiverFor(ctx, upcoming.id);
    if (daysOut <= 7 && waiver?.status !== 'signed') {
      const studio = ctx.studios[upcoming.studioId];
      if (studio) {
        const cadence = buildConfirmationCadence({
          booking: upcoming,
          customer: ctx.customer,
          artist: ctx.artists[upcoming.artistId],
          studio,
          waiverStatus: waiver?.status ?? 'not-sent',
        });
        const step = cadence.find((s) => s.stage === 'seven-day') ?? cadence[0];
        if (step) {
          return {
            kind: 'confirm-waiver',
            label: 'Confirm + waiver',
            body: step.body,
            why: `Session is ${Math.round(daysOut)} days out and the waiver is not signed`,
          };
        }
      }
    }
  }

  // 5 · Long quiet with a piece still open.
  const quietDays = daysSinceLastContact(ctx);
  const unfinished = continuablePiecesFor(ctx.pieces, ctx.customer);
  if (quietDays !== null && quietDays >= 90 && unfinished.length > 0) {
    return {
      kind: 'reactivation',
      label: 'Reactivation tap',
      body: `Hey ${name}, long time no see. Your ${unfinished[0]!.title || unfinished[0]!.style} is still only part done - want me to get the next session in with your artist?`,
      why: `${Math.round(quietDays)} days quiet with an unfinished piece on file`,
    };
  }

  return null;
}
